import moment from 'moment';
import XLSX from 'xlsx';

export const readSpreadsheetFile = (file) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
        reader.onload = (event) => {
            const data = new Uint8Array(event.target.result);
            const parsedData = XLSX.read(data, {
                type: 'array',
                raw: true,
                cellDates: false,
            });

            if (parsedData) {
                resolve(parsedData);
            } else {
                reject({ message: 'Error reading file' });
            }
        };

        reader.readAsArrayBuffer(file);
    });
};

export const getSheetData = (workbook, hasHeader, sheetIndex = 0) => {
    const name = workbook.SheetNames[sheetIndex];
    const sheet = workbook.Sheets[name];
    const options = !hasHeader ? { raw: true, header: 1 } : undefined; // { header: 1 } means convert spreadsheet as array of arrays
    const sheetData = XLSX.utils.sheet_to_json(sheet, options); // https://docs.sheetjs.com/docs/api/utilities#array-output
    return sheetData.filter((data) => data.length !== 0);
};

export const getSheetHeaders = (workbook, sheetIndex = 0) => {
    const sheetData = getSheetData(workbook, sheetIndex);
    return Object.keys(sheetData[0]);
};

const CELL_ERROR_MESSAGES = {
    text: {
        type: () => 'Wrong data type!\nExpected type is string!',
        maxChar: (maxChar) => `Too long value!\nIt must be equal or lower than ${maxChar} characters!`,
    },
    date: {
        type: () => 'Wrong data type!\nExpected type is date!',
        format: (format) => `Wrong date format!\nExpected format: ${format}`,
    },
    number: {
        type: () => 'Wrong data type!\nExpected type is number!',
        max: () =>
            `Number is too big!\nExpected number in between -${Number.MAX_SAFE_INTEGER} and ${Number.MAX_SAFE_INTEGER}!`,
    },
};

const SERVER_CELL_ERROR_MESSAGES = {
    'can-not-found': () => 'Can not found!\nServer expected to found this on database!',
    'not-related': (otherParamTitle) =>
        `Not related each other!\nServer expected this related with "${otherParamTitle}" column!`,
    default: () => 'Server error!\nServer returns validation error for this cell!',
};

export class CellError extends Error {
    constructor(value, ...params) {
        super(...params);
        this.value = value;
    }
}

const parseCell = (rules, cellValue) => {
    switch (rules.type) {
        case 'text':
            if (typeof cellValue !== 'string' || (rules.optional && cellValue !== ''))
                return new CellError(cellValue, CELL_ERROR_MESSAGES.text.type());
            if (cellValue.length > Number(rules.maxChar))
                return new CellError(cellValue, CELL_ERROR_MESSAGES.text.maxChar(rules.maxChar));

            return cellValue;

        case 'date':
            if (typeof cellValue !== 'string' || (rules.optional && cellValue !== ''))
                return new CellError(cellValue, CELL_ERROR_MESSAGES.date.type());

            const dateValue = moment(cellValue, rules.format);
            if (!dateValue.isValid()) return new CellError(cellValue, CELL_ERROR_MESSAGES.date.format(rules.format));

            return cellValue;

        case 'number':
            const numberValue = Number(cellValue);

            if (typeof cellValue === 'undefined') {
                return cellValue;
            } else if (typeof cellValue === 'string' && cellValue === 'NaN') {
                return cellValue;
            } else if (!numberValue && numberValue !== 0) {
                return new CellError(cellValue, CELL_ERROR_MESSAGES.number.type());
            } else if (numberValue > Number.MAX_SAFE_INTEGER || numberValue < -Number.MAX_SAFE_INTEGER) {
                return new CellError(cellValue, CELL_ERROR_MESSAGES.number.max());
            }
            return cellValue;

        default:
            return cellValue;
    }
};

const parseCellWithServerError = (fields, field, cellValue, error) => {
    if (!error || field.type !== 'id') {
        return cellValue;
    }
    if (error.parameter !== field.dataId && error.parameters != null && !error.parameters.includes(field.dataId)) {
        return cellValue;
    }
    const otherParam = error.parameters && error.parameters.find((p) => p !== field.dataId);
    const otherParamTitle = otherParam && fields.find((f) => f.dataId === otherParam)?.title;

    return new CellError(
        cellValue,
        SERVER_CELL_ERROR_MESSAGES[error.type]?.(error.parameter || otherParamTitle) ??
            SERVER_CELL_ERROR_MESSAGES.default,
    );
};

function firstRowToPeriodTitles(sheetData, fields) {
    let _sheetData = [...sheetData];
    const firstRow = _sheetData.shift();

    const mappedFields = [...fields].map((field, index) => {
        if (field.type !== 'number') return field;
        return { ...field, title: firstRow[index] };
    });

    return [_sheetData, mappedFields];
}

export const parseSheet = (workbook, fields, generateTitles, sheetIndex = 0) => {
    const periodTitlesFromFirstRow = !generateTitles;
    let sheetData = getSheetData(workbook, false, sheetIndex);

    let mappedFields = [...fields];
    const parsedData = [];

    if (periodTitlesFromFirstRow) {
        [sheetData, mappedFields] = firstRowToPeriodTitles(sheetData, fields);
    }

    for (const [index, row] of sheetData.entries()) {
        const newRow = {};
        if (periodTitlesFromFirstRow && index === 0) {
        }
        for (const [index, field] of mappedFields.entries()) {
            newRow[field.id] = parseCell(field, row[index]);
        }
        parsedData.push(newRow);
    }

    return [parsedData, mappedFields];
};

export const parseSheetToRequest = (workbook, fields, generateTitles, sheetIndex = 0) => {
    const periodTitlesFromFirstRow = !generateTitles;
    let sheetData = getSheetData(workbook, false, sheetIndex);
    let mappedFields = [...fields];
    const requestData = [];

    if (periodTitlesFromFirstRow) {
        [sheetData, mappedFields] = firstRowToPeriodTitles(sheetData, fields);
    }

    // Which columns are the "years" (number fields)
    const yearFieldIndexes = mappedFields
        .map((f, idx) => ({ f, idx }))
        .filter(({ f }) => f?.type === 'number')
        .map(({ idx }) => idx);

    const isBlank = (v) => v === undefined || v === null || v === '';

    for (const row of sheetData) {
        //If ALL year cells are blank => ignore entire row
        const hasAnyYearValue = yearFieldIndexes.some((i) => !isBlank(row[i]));
        if (!hasAnyYearValue) continue;

        const newRow = {};
        for (const [index, field] of mappedFields.entries()) {
            if (!field.type) continue;
            const key = field.dataId || field.title;
            const value = row[index];
            // Ignore blank year cells (don't include the key at all)
        if (field.type === 'number' && isBlank(value)) continue;
        
        newRow[key] = value;    
        }
        requestData.push(newRow);
    }

    return requestData;
};

export const parseSheetWithServerErrors = (errors, workbook, fields, sheetIndex = 0) => {
    const sheetData = getSheetData(workbook, false, sheetIndex);

    const parsedData = sheetData.map((row, rowIndex) => {
        const newRow = {};
        for (const [index, field] of fields.entries()) {
            newRow[field.id] = parseCellWithServerError(fields, field, row[index], errors?.[rowIndex]);
        }
        return newRow;
    });

    return parsedData;
};
